import type { ProjectDocument } from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { apiError } from "../api-errors.ts";
import { useSession } from "../session.tsx";
import { documentRefusal } from "./EditAccessDialog.tsx";
import { projectKeys } from "./queries.ts";
import { deleteDocumentRequest } from "./upload.ts";

/**
 * Confirms deleting a file that holds content, stating what deletion does before anything is
 * sent. A refusal stays in the dialog; every outcome refreshes the file list.
 */
export function DeleteFileDialog(props: {
  projectId: string;
  document: ProjectDocument | null;
  onClose: () => void;
  /** The row's actions button, focused again on close. */
  returnFocus?: HTMLElement | null;
}) {
  const { projectId, document: d, onClose } = props;
  const { t } = useTranslation();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: async (doc: ProjectDocument) => {
      const res = await deleteDocumentRequest(projectId, doc.id, csrfToken);
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onSuccess: (_data, doc) => {
      toast(t("files.deleted", { title: doc.title }));
      onClose();
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: projectKeys.documents(projectId) }),
  });
  const close = () => {
    if (!remove.isPending) {
      remove.reset();
      onClose();
    }
  };
  return (
    <AppDialog
      returnFocus={props.returnFocus}
      open={d !== null}
      onClose={close}
      title={d ? t("files.deleteTitle", { title: d.title }) : ""}
      description={t("files.deleteLead")}
    >
      {d ? (
        <div className="flex flex-col gap-5">
          <ul className="flex list-disc flex-col gap-1.5 pl-5 text-sm text-fg-secondary">
            {(["retrieval", "citations", "wiki", "purge"] as const).map((effect) => (
              <li key={effect}>{t(`files.deleteEffects.${effect}`)}</li>
            ))}
          </ul>
          {remove.error ? <Notice tone="danger">{documentRefusal(remove.error)}</Notice> : null}
          <DialogFooter>
            <Button variant="outline" disabled={remove.isPending} onClick={close}>
              {t("files.cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => remove.mutate(d)}
            >
              {remove.isPending ? t("files.deleting") : t("files.deleteConfirm")}
            </Button>
          </DialogFooter>
        </div>
      ) : null}
    </AppDialog>
  );
}
